import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type AdminMenuCategory =
  | 'workspace'
  | 'access'
  | 'operations'
  | 'billing'
  | 'account';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  href: string;
  badge?: string;
  badgeColor?: string;
  category: AdminMenuCategory;
  /** If set, the item is only shown when the user holds this action permission. */
  actionId?: string;
}

interface AdminSidebarProps {
  items: NavItem[];
  onNavigate?: (href: string) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const ChevronDownIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    className="w-4 h-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19 14l-7 7m0 0l-7-7m7 7V3"
    />
  </svg>
);

const ChevronUpIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    className="w-4 h-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M5 10l7-7m0 0l7 7m-7-7v18"
    />
  </svg>
);

const CATEGORY_ORDER: AdminMenuCategory[] = [
  'workspace',
  'access',
  'operations',
  'billing',
  'account',
];
const CATEGORY_META: Record<AdminMenuCategory, { label: string }> = {
  workspace: {
    label: 'Workspace',
  },
  access: {
    label: 'Access Control',
  },
  operations: {
    label: 'Operations',
  },
  billing: {
    label: 'Billing',
  },
  account: {
    label: 'Account',
  },
};

export const AdminSidebar: React.FC<AdminSidebarProps> = ({
  items,
  onNavigate,
  isOpen,
  setIsOpen,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(CATEGORY_ORDER)
  );

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const handleNavigate = (href: string) => {
    if (onNavigate) {
      onNavigate(href);
    } else {
      navigate(href);
    }
  };

  const groupedItems = CATEGORY_ORDER.reduce(
    (acc, category) => {
      acc[category] = items.filter(item => item.category === category);
      return acc;
    },
    {} as Record<AdminMenuCategory, NavItem[]>
  );

  return (
    <>
      {/* Sidebar overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 lg:hidden z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-screen z-40 lg:z-0 lg:static transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        style={{
          width: '280px',
          backgroundColor: 'var(--bg-secondary)',
          borderRight: '1px solid var(--border)',
          overflowY: 'auto',
        }}
      >
        <div
          className="sticky top-0 p-4 border-b"
          style={{
            borderColor: 'var(--border)',
            backgroundColor: 'var(--bg-secondary)',
          }}
        >
          <p
            className="text-[11px] font-bold uppercase tracking-[0.18em]"
            style={{ color: 'var(--text-secondary)' }}
          >
            Console
          </p>
        </div>

        <nav className="p-4 space-y-2">
          {CATEGORY_ORDER.map(category => {
            const categoryItems = groupedItems[category];
            const isExpanded = expandedCategories.has(category);
            if (categoryItems.length === 0) return null;

            return (
              <div key={category}>
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center justify-between px-2 py-2 rounded-lg text-xs font-semibold uppercase tracking-[0.14em] transition-colors"
                  style={{
                    color: 'var(--text-secondary)',
                    backgroundColor: 'transparent',
                    border: 'none',
                  }}
                >
                  <span className="text-left">
                    {CATEGORY_META[category].label}
                  </span>
                  {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </button>

                {isExpanded && (
                  <div
                    className="mt-1 ml-2 pl-3 border-l space-y-1"
                    style={{ borderColor: 'var(--border)' }}
                  >
                    {categoryItems.map(item => {
                      const isActive =
                        location.pathname === item.href ||
                        (item.href !== '/console' &&
                          location.pathname.startsWith(`${item.href}/`));
                      return (
                        <button
                          key={item.id}
                          onClick={() => {
                            handleNavigate(item.href);
                            setIsOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all group"
                          style={{
                            backgroundColor: isActive
                              ? 'rgba(var(--accent-rgb), 0.1)'
                              : 'transparent',
                            color: isActive
                              ? 'var(--accent)'
                              : 'var(--text-secondary)',
                            border: isActive
                              ? '1px solid rgba(var(--accent-rgb), 0.16)'
                              : '1px solid transparent',
                          }}
                        >
                          <div
                            className="w-5 h-5 flex items-center justify-center flex-shrink-0"
                            style={{
                              color: isActive
                                ? 'var(--accent)'
                                : 'var(--text-secondary)',
                            }}
                          >
                            {item.icon}
                          </div>
                          <span className="font-medium flex-1 text-left">
                            {item.label}
                          </span>
                          {item.badge && (
                            <span
                              className="px-2 py-0.5 rounded-full text-xs font-semibold text-white flex-shrink-0"
                              style={{
                                backgroundColor:
                                  item.badgeColor === 'warning'
                                    ? '#f59e0b'
                                    : 'var(--accent)',
                              }}
                            >
                              {item.badge}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
};

export default AdminSidebar;
